/* eslint-disable no-unused-vars */
import { useCallback, useEffect, useId, useMemo, memo } from 'react';
import {
  CustomInput,
  Label,
  CopyString,
  ToggleBtn,
  IconButton,
  Buttons,
  NotificationModal,
} from 'components';
import { useDispatch, useSelector } from 'react-redux';
import { formErrorsCampaign_action, creativesActions } from 'store/actions';
import { DeleteIcon, DeleteUploadApk, Delete } from 'assets/icons';
import {
  selectSortedCreativeList,
  select_creatives_list,
  getCountOfActiveCreatives,
  getCountOfNotDeletedCreatives,
  getMaxActiveBanners,
  select_campaign_env,
  isAdminSelector,
} from 'store/selectors';
import { campaignEnvironment } from 'constants';
import { LengthCounter } from '../index';
import * as S from './CreativeNameToggleDelete.styled';
import { MaxActiveBannersModal } from './MaxActiveBannersModal';
import { useComputed, useSignal } from '@preact/signals-react';

const MAX_SIZE = 90;

export const CreativeNameToggleDelete = memo(
  ({
    onChangeParam,
    isRuCampaigns,
    isTokenEditable,
    initialTokens,
    isNewCreative,
    validateFields,
  }) => {
    const dispatch = useDispatch();
    const inputId = useId();
    const tokenId = useId();
    const currentCreativeName = useSelector((state) => state?.creatives?.selectedCreative?.name);
    const creativesList = useSelector((state) => state?.creatives?.creativesList);
    const currentCreativeToken = useSelector(
      (state) => state?.creatives?.selectedCreative?.creative_id_token,
    );

    const currentBannerId = useSelector((state) => state?.creatives?.selectedCreative?.bn_bannerid);
    const currentStatus = useSelector((state) => state?.creatives?.selectedCreative?.status);
    const countOfActiveCreatives = useSelector(getCountOfActiveCreatives);
    const maxActiveBanners = useSelector(getMaxActiveBanners);
    const countOfNotDeletedCreatives = useSelector(getCountOfNotDeletedCreatives);
    const isAdmin = useSelector(isAdminSelector);
    const environment = useSelector(select_campaign_env);

    const confirmDeleteCreative$ = useSignal(false);
    const isMoreThenThreeCreatives$ = useSignal(false);

    useEffect(() => {
      // handle validation when tab's changed
      validateFields();
    }, [currentBannerId]);

    const onCreativeActiveChange = useCallback(
      (checked) => {
        if (checked) {
          if (countOfActiveCreatives >= maxActiveBanners) {
            isMoreThenThreeCreatives$.value = true;
            return;
          }
        }
        dispatch(
          creativesActions.updateProperty({
            propField: 'status',
            newValue: checked ? 0 : 1,
          }),
        );
      },
      [countOfActiveCreatives, maxActiveBanners],
    );

    const isNameError = useMemo(() => currentCreativeName?.length < 2, [currentCreativeName]);

    const isUniqueToken =
      creativesList.length < 2 ||
      creativesList.every(
        (item) =>
          currentBannerId === item.bn_bannerid || item.creative_id_token !== currentCreativeToken,
      );

    const maxCharsError$ = useSignal(false);

    const deleteCreative = useCallback(() => {
      dispatch(creativesActions.deleteSelectedCreative());
      confirmDeleteCreative$.value = false;
    }, []);

    const isBannerId = useMemo(() => {
      if (typeof currentBannerId === 'number') return true;
      return false;
    }, [currentBannerId]);

    const getErrorMsg = () => {
      if (maxCharsError$.value) {
        return `Text exceeds the ${MAX_SIZE}-character limit. Please shorten your input and try again.`;
      }
      return 'Please Insert Creative Set Name';
    };

    //const initialToken = initialTokens?.find((item) => item.id === currentBannerId);
    const isDisabledToEdit = !isAdmin && environment === campaignEnvironment.edit && !isNewCreative;
    const isTokenDisabledToEdit = !isTokenEditable && currentCreativeToken && isDisabledToEdit;
    // || (initialToken && currentCreativeToken === initialToken);

    return (
      <S.Container>
        <S.InputBox>
          <S.DisabledBox isDisabled={isDisabledToEdit}>
            <S.LabelBox>
              <Label text='Creative Set Name:' variant='label' />
              <LengthCounter maxSize={MAX_SIZE} str={currentCreativeName} />
            </S.LabelBox>
          </S.DisabledBox>
          <S.InputWrap>
            <S.DisabledBox isDisabled={isDisabledToEdit}>
              <CustomInput
                onBlur={() => {}}
                value={currentCreativeName}
                autoComplete='off'
                id={inputId}
                name={inputId}
                onChange={(e) => {
                  maxCharsError$.value = e.target.value.length > MAX_SIZE;
                  onChangeParam(e, 'name', MAX_SIZE);
                }}
                placeholder='Creative Set name'
                error={isNameError || maxCharsError$.value}
                errorText={getErrorMsg()}
                inputStyle={{
                  borderTopRightRadius: '0',
                  borderBottomRightRadius: '0',
                  minHeight: '48px',
                }}
              />
            </S.DisabledBox>
            <ToggleBtn
              style={{
                border: '1px solid #E2E8F0',
                minHeight: '48px',
                padding: '12px 16px',
                borderLeft: 'none',
              }}
              id='enableFieldCreatives'
              isChecked={currentStatus === 0}
              onChange={onCreativeActiveChange}
              lableOn='Active'
              labelOff='Disabled'
            />
            <IconButton.NormalSize
              icon={Delete}
              disabled={countOfNotDeletedCreatives === 1}
              type={IconButton.btnTypes.tertiary}
              onClick={() => {
                confirmDeleteCreative$.value = true;
              }}
              style={{
                border: '1px solid #E2E8F0',
                minWidth: '48px',
                height: '48px',
                borderLeft: 'none',
                borderTopLeftRadius: '0',
                borderBottomLeftRadius: '0',
              }}
            />
          </S.InputWrap>
          {isBannerId && <CopyString label='ID' text={currentBannerId?.toString()} />}
        </S.InputBox>
        {useComputed(() => (
          <MaxActiveBannersModal
            isOpen={isMoreThenThreeCreatives$.value}
            onClose={() => (isMoreThenThreeCreatives$.value = false)}
            maxActiveBanners={maxActiveBanners}
          />
        ))}
        {useComputed(() => {
          return (
            confirmDeleteCreative$.value && (
              <NotificationModal
                title=''
                id='deleteCreative'
                iconType={NotificationModal.iconTypes.info}
                iconSign='i'
                onConfirm={deleteCreative}
                confirmBtnType={NotificationModal.btnTypes.PrimaryNormal}
                confirmBtnText='Ok'
              >
                Changes will be applied after saving the campaign
              </NotificationModal>
            )
          );
        })}
        {isRuCampaigns && (
          <S.DisabledBox isDisabled={isTokenDisabledToEdit}>
            <S.InputBox>
              <S.LabelBox>
                <Label text='Token:' variant='label' />
              </S.LabelBox>
              <CustomInput
                value={currentCreativeToken}
                autoComplete='off'
                id={tokenId}
                name={tokenId}
                onChange={(e) => {
                  onChangeParam(e, 'creative_id_token');
                }}
                error={!currentCreativeToken || (currentCreativeToken && !isUniqueToken)}
                errorText={
                  !isUniqueToken && currentCreativeToken
                    ? 'Duplicate Creative ID. Each creative targeting RU must have a unique ID.'
                    : 'Creative ID is required for RU campaigns.'
                }
                inputStyle={{
                  borderTopRightRadius: '0',
                  borderBottomRightRadius: '0',
                  minHeight: '48px',
                }}
              />
            </S.InputBox>
          </S.DisabledBox>
        )}
      </S.Container>
    );
  },
);

CreativeNameToggleDelete.displayName = 'CreativeNameToggleDelete';
